import {
	Component,
	EventEmitter,
	Input,
	OnInit,
	Optional,
	Output,
	ViewChild,
} from '@angular/core';
import { $localize } from '@angular/localize/init';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { DxDataGridComponent } from 'devextreme-angular';
import { ValueChangedEvent } from 'devextreme/ui/select_box';
import type { KeyDownEvent } from 'devextreme/ui/text_box';
import { Map } from 'immutable';
import {
	BehaviorSubject,
	concat,
	filter,
	firstValueFrom,
	map,
	merge,
	Observable,
	of,
	ReplaySubject,
	takeUntil,
	tap,
	withLatestFrom,
} from 'rxjs';

import { DxOperations } from '../../../core/dx-types';
import { ComponentIcons } from '../../../ui/app-components.icons';
import { DcIcons } from '../../../ui/app-dc.icons';
import { DcLoaders } from '../../../ui/app-loaders';
import {
	ColumnConfigItemEntity,
	ColumnErrorTypes,
	ColumnReferrer,
} from '../../domain/column-config-item.entity';
import {
	ColumnConfigListEntity,
	ColumnErrorKey,
	HASHED_COLUMN_TYPES,
} from '../../domain/column-config-list.entity';
import { SubscriptionErrorKey } from '../../domain/diagnostic-errors.entity';
import { SubscriptionProvider } from '../../providers/subscription.provider';
import { ColumnListProvider } from './column-list.provider';

type ColumnsConfigMode = 'SELECTION' | 'PARAMETER';

type Reference = 'profile' | 'colModel' | 'filterModel' | 'none';
type ColumnUsageOverlayDetails = {
	columnId: string | undefined;
	usages: Array<ColumnReferrer>;
	type: Reference;
};

@Component({
	selector: 'app-columns-config',
	templateUrl: './columns-config.component.html',
	styleUrls: ['./columns-config.component.scss'],
	inputs: [
		'columns',
		'isInViewMode',
		'isInModelConfigMode',
		'columnsConfigMode',
	],
	providers: [ColumnListProvider],
	exportAs: 'ColumnsGrid',
})
export class ColumnsConfigComponent extends DcBaseComponent implements OnInit {
	protected readonly DcLoaders = DcLoaders;
	protected readonly ColumnErrorTypes = ColumnErrorTypes;
	protected readonly DcIcons = DcIcons;
	protected readonly HASHED_COLUMN_TYPES = HASHED_COLUMN_TYPES;

	@Input()
	public isInModelConfigMode = false;

	@Output()
	public updatedColumnList = new EventEmitter<ColumnConfigListEntity>();

	public vo$: Observable<{
		columnList: ColumnConfigListEntity;
		enableHashedAll: boolean;
		enableSelectedInSourceAll: boolean;
		isInViewMode: boolean;
		isInModelConfigMode: boolean;
		columnsConfigMode: ColumnsConfigMode;
		selectedColumnCount: number;
		openedOverlay: ColumnUsageOverlayDetails;
	}>;

	public readonly MapErrors: { [k: string]: string } = {
		[ColumnErrorTypes.DUPLICATED]: $localize`:i18n=@@general.form.errors.duplicated:`,
		[ColumnErrorTypes.NOT_NORMALIZE]: $localize`:i18n=@@general.form.errors.unauthorizedCharacters:`,
		[ColumnErrorTypes.EMPTY]: $localize`:i18n=@@general.form.errors.required:`,
		[ColumnErrorTypes.MAX_LENGTH_DESC]: $localize`:i18n=@@general.form.errors.maxlength:`,
	};

	private readonly columnList$ = new ReplaySubject<ColumnConfigListEntity>(1);
	private readonly openedOverlay$ =
		new BehaviorSubject<ColumnUsageOverlayDetails>({
			columnId: undefined,
			usages: [],
			type: 'none',
		});

	@ViewChild(DxDataGridComponent)
	private readonly grid: DxDataGridComponent | null = null;

	protected readonly ComponentIcons = ComponentIcons;
	public constructor(
		@Optional()
		private readonly subscriptionProvider: SubscriptionProvider,
		private readonly columnListProvider: ColumnListProvider
	) {
		super();
		this.cmpId = 'columns-config';

		const enableHashedAll$ = this.columnList$.pipe(
			map((list) =>
				list.elements.some(
					(el) => el.isActive && HASHED_COLUMN_TYPES.includes(el.columnType)
				)
			)
		);

		const enableSelectedInSourceAll$ = this.columnList$.pipe(
			map((list) => list.elements.some((el) => el.referencedBy.total === 0))
		);

		const selectedColumnCount$ = this.columnList$.pipe(
			map((list) =>
				list.elements.reduce<number>(
					(acc, curr) => (curr.isSelectedInSource ? acc + 1 : acc),
					0
				)
			)
		);

		this.vo$ = combineAll({
			columnList: this.columnList$,
			enableHashedAll: enableHashedAll$,
			enableSelectedInSourceAll: enableSelectedInSourceAll$,
			isInViewMode: merge(
				of(false),
				this.toObservable<boolean>('isInViewMode')
			),
			isInModelConfigMode: merge(
				of(false),
				this.toObservable<boolean>('isInModelConfigMode')
			),
			columnsConfigMode: concat(
				of<ColumnsConfigMode>('PARAMETER'),
				this.toObservable<ColumnsConfigMode>('columnsConfigMode')
			),
			selectedColumnCount: selectedColumnCount$,
			openedOverlay: this.openedOverlay$,
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();
		this.handleSubscription();
	}

	public search(searchTerm: string): void {
		if (this.grid === null) {
			return;
		}
		this.grid.instance.searchByText(searchTerm.trim());
	}

	public onTextBoxKeyDown(e: KeyDownEvent): void {
		if (!e.event) {
			return;
		}

		const keys: ReadonlyArray<KeyboardEvent['key']> = [
			'ArrowLeft',
			'ArrowRight',
			'ArrowUp',
			'ArrowDown',
		];

		if (keys.includes(e.event.key)) {
			e.event.stopPropagation();
		}
	}

	public async updateIsSelectedInSource(
		id: string,
		checked: boolean
	): Promise<void> {
		const columnList = await firstValueFrom(this.columnList$);
		const updatedList = columnList.updateIsSelectedInSource([id], checked);
		this.columnList$.next(updatedList);
	}

	public async toggleSelectedInSourceAllRows(checked: boolean): Promise<void> {
		const columnList = await firstValueFrom(this.columnList$);
		const ids = columnList.elements
			.filter(
				(v) => v.isSelectedInSource === !checked && v.referencedBy.total === 0
			)
			.keySeq()
			.toArray();

		const updatedList = columnList.updateIsSelectedInSource(ids, checked);
		this.columnList$.next(updatedList);
	}

	public async updateIsActive(id: string, checked: boolean): Promise<void> {
		const columnList = await firstValueFrom(this.columnList$);
		const updatedList = columnList.updateIsActive([id], checked);
		this.columnList$.next(updatedList);
	}

	public async toggleActiveAllRows(checked: boolean): Promise<void> {
		const columnList = await firstValueFrom(this.columnList$);
		const ids = columnList.elements
			.filter((v) => v.isActive === !checked)
			.keySeq()
			.toArray();

		const updatedList = columnList.updateIsActive(ids, checked);
		this.columnList$.next(updatedList);
	}

	public toggleAllTriggered($event: MouseEvent): void {
		$event.stopPropagation();
	}

	public async updateUsedAlias(
		id: string,
		$event: ValueChangedEvent
	): Promise<void> {
		const newAlias = $event.value as string;
		const columnList = await firstValueFrom(this.columnList$);
		const updatedList = columnList.updateAlias(id, newAlias);
		this.columnList$.next(updatedList);
	}

	public async updateDescription(
		id: string,
		$event: ValueChangedEvent
	): Promise<void> {
		const newDescription = $event.value as string;
		const columnList = await firstValueFrom(this.columnList$);
		const updatedList = columnList.updateDescription(id, newDescription);
		this.columnList$.next(updatedList);
	}

	public async updateIsHashed(id: string, checked: boolean): Promise<void> {
		const columnList = await firstValueFrom(this.columnList$);
		const updatedList = columnList.updateIsHashed([id], checked);
		this.columnList$.next(updatedList);
	}

	public async toggleHashableAllRows(checked: boolean): Promise<void> {
		const columnList = await firstValueFrom(this.columnList$);
		const ids = columnList.elements
			.filter(
				(v) =>
					v.isHashed === !checked &&
					v.isActive &&
					HASHED_COLUMN_TYPES.includes(v.columnType)
			)
			.keySeq()
			.toArray();

		const updatedList = columnList.updateIsHashed(ids, checked);
		this.columnList$.next(updatedList);
	}

	public filterColumnConfigErrors(): void {
		if (!this.grid) {
			return;
		}

		const currentFilter = this.grid.instance.filter();
		if (currentFilter) {
			this.grid.instance.clearFilter();
			return;
		}

		this.grid.instance.filter([
			(data: ColumnConfigItemEntity): boolean =>
				data.errors && data.errors.size !== 0,
			'and',
			['isSelectedInSource', DxOperations.Equal, true],
		]);
	}

	public clearFilter(): void {
		if (!this.grid) {
			return;
		}

		const currentFilter = this.grid.instance.filter();

		if (!currentFilter) {
			return;
		}
		this.grid.instance.clearFilter();
	}

	public async getActiveColumns(): Promise<
		Map<string, ColumnConfigItemEntity>
	> {
		const list = await firstValueFrom(this.columnList$);
		return list.elements.filter((item) => item.isActive);
	}

	public async getUpdatedColumns(): Promise<
		Map<string, ColumnConfigItemEntity>
	> {
		const list = await firstValueFrom(this.columnList$);
		return list.elements;
	}

	public closeColumnsPopover(): void {
		this.openedOverlay$.next({
			columnId: undefined,
			usages: [],
			type: 'none',
		});
	}

	public async openReferences(
		event: Event,
		columnConfig: ColumnConfigItemEntity,
		type: Reference
	): Promise<void> {
		event.stopPropagation();

		const previous = await firstValueFrom(this.openedOverlay$);
		if (previous.columnId === columnConfig.id) {
			this.openedOverlay$.next({
				...previous,
				columnId: undefined,
				usages: [],
				type: 'none',
			});
			return;
		}

		let usages: Array<ColumnReferrer> = [];
		if (type === 'profile') {
			usages = columnConfig.referencedBy.profiles;
		} else if (type === 'colModel') {
			usages = columnConfig.referencedBy.columnModels;
		} else if (type === 'filterModel') {
			usages = columnConfig.referencedBy.filterModels;
		}

		this.openedOverlay$.next({
			...previous,
			columnId: columnConfig.id,
			usages,
			type,
		});
	}

	private handleSubscription(): void {
		this.toObservable<Map<string, ColumnConfigItemEntity>>('columns')
			.pipe(
				takeUntil(this.onDestroy$),
				withLatestFrom(this.toObservable<boolean>('isInViewMode')),
				tap(([items, isInViewMode]) => {
					this.columnListProvider.createList(
						isInViewMode,
						this.isInModelConfigMode
					);
					const columnList = this.columnListProvider.setListElements(items);
					this.columnList$.next(columnList);
				})
			)
			.subscribe();

		this.columnList$
			.pipe(
				takeUntil(this.onDestroy$),
				withLatestFrom(this.toObservable<boolean>('isInViewMode')),
				filter(([, isInViewMode]) => !isInViewMode),
				tap(([list]) => {
					if (list.errors.has(ColumnErrorKey.NoColumnsSelectedInSource)) {
						this.subscriptionProvider.addCriticalError(
							$localize`:i18n=@@subscription.diagnosticError.columns.notSelected:`,
							SubscriptionErrorKey.NoSelectColumns
						);
					} else {
						this.subscriptionProvider.removeError(
							SubscriptionErrorKey.NoSelectColumns
						);
					}

					if (list.errors.has(ColumnErrorKey.InvalidColumns)) {
						this.subscriptionProvider.addCriticalError(
							$localize`:i18n=@@subscription.diagnosticError.columns.invalid:`,
							SubscriptionErrorKey.InvalidSubscriptionColumns
						);
					} else {
						this.subscriptionProvider.removeError(
							SubscriptionErrorKey.InvalidSubscriptionColumns
						);
					}

					this.updatedColumnList.emit(list);
				})
			)
			.subscribe();
	}
}
